import { useState, useEffect } from 'react'
import { useYear } from '../auth/YearContext'
import {
  subscribeYear,
  setVillkoratTillskottCloud,
  type CloudYearDoc,
} from '../lib/cloudStore'
import { WinButtons, currentSize, type CardSizeProps } from './WinButtons'

const fmt = (n: number) =>
  n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

/**
 * Seed från signerade ÅR 2025 (Bolagsverket 2026-06-28).
 * LSI (förening) har inget villkorat aktieägartillskott.
 * Källa: Not 2 – Villkorat aktieägartillskott.
 */
const AR_SEED: Record<string, Record<number, number>> = {
  occdec: { 2025: 1_788_142 },
  'linesp-ab': { 2025: 900_351 },
  // linesp-fo: inget
}

interface Props extends CardSizeProps {
  companyId: string
  companyName: string
}

export function VillkoratTillskottCard(props: Props) {
  const { companyId, companyName } = props
  const size = currentSize(props)
  const { year } = useYear()
  const [data, setData] = useState<CloudYearDoc>({ year, months: [] })
  const [editing, setEditing] = useState(false)
  const [input, setInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setErr(null)
    setEditing(false)
    try {
      return subscribeYear(companyId, year, setData)
    } catch (e) {
      setErr((e as Error).message)
      return () => {}
    }
  }, [companyId, year])

  const seeded = AR_SEED[companyId]?.[year]
  const fromCloud = data.villkoratTillskott
  const hasCloud = fromCloud != null
  const belopp = hasCloud ? fromCloud : seeded
  const isSeedOnly = !hasCloud && seeded != null
  const isEmpty = belopp == null
  const updated = data.villkoratTillskottUpdated
    ? new Date(data.villkoratTillskottUpdated).toLocaleDateString('sv-SE')
    : null

  const save = async () => {
    const n = Number(input.replace(/\s/g, '').replace(',', '.'))
    if (isNaN(n)) return
    setSaving(true)
    setErr(null)
    try {
      await setVillkoratTillskottCloud(companyId, year, n)
      setEditing(false)
      setInput('')
    } catch (e) {
      setErr('Kunde inte spara: ' + (e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (size === 'min') {
    return (
      <div className="card card--collapsed" style={{ borderLeftColor: '#8b5cf6' }}>
        <div className="card__header-row">
          <span className="card__label" style={{ margin: 0 }}>Villkorat tillskott</span>
          <span className="card__mini-value">
            {isEmpty ? '\u2014' : `${fmt(belopp!)} kr`}
          </span>
          <WinButtons current="min" {...props} />
        </div>
      </div>
    )
  }

  return (
    <div
      className={'card card--accent' + (size === 'med' ? ' card--medium' : '')}
      style={{ borderLeftColor: '#8b5cf6' }}
    >
      <div className="card__header-row">
        <div className="card__label" style={{ margin: 0 }}>
          Villkorat aktieägartillskott {'\u2014'} {companyName}
        </div>
        <WinButtons current={size} {...props} />
      </div>
      <div className="card__value">
        {isEmpty ? '\u2014' : `${fmt(belopp!)} kr`}
      </div>
      <div className="card__sub">
        {isEmpty
          ? 'Inget tillskott inlagt'
          : isSeedOnly
            ? `Från ÅR ${year} (ej återbetalat per balansdag)`
            : updated
              ? `Senast uppdaterad ${updated}`
              : 'Manuellt inlagt'}
      </div>
      <div className="card__divider" />
      {!editing ? (
        <button
          className="cs__button"
          style={{ fontSize: '0.85rem' }}
          onClick={() => {
            setInput(belopp != null ? String(belopp) : '')
            setEditing(true)
          }}
        >
          {isEmpty ? '+ Lägg in belopp' : '\u270f\ufe0f Uppdatera belopp'}
        </button>
      ) : (
        <div className="inline-edit">
          <input
            type="text"
            inputMode="decimal"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="1788142"
            autoFocus
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') setEditing(false)
            }}
          />
          <button onClick={save} disabled={saving}>
            {saving ? 'Sparar\u2026' : 'Spara'}
          </button>
        </div>
      )}
      {err && (
        <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 6 }}>
          {err}
        </div>
      )}
      {size === 'max' && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
          Ej återbetalat villkorat aktieägartillskott. Uppdatera vid tillskott/återbetalning.
        </div>
      )}
    </div>
  )
}
